'use strict'

import { getBreakpoint, getAppPlatform } from '@/utils/app/detect'
import type { MetaDataArticle } from '@/types/cms'

declare global {
  interface Window {
    dataLayer: Array<object>
  }
}

export type GtmParams = {
  readonly trackingPath: string
  readonly article?: MetaDataArticle
  readonly isWebView: boolean
  readonly sectionName: string
  readonly gtmKeywords?: Array<string>
}

export default function gtmInit(params: GtmParams): void {
  window.dataLayer = [createPageviewData(params)]

  const gtmScript = document.createElement('script')
  gtmScript.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-PCPP2XC');`
  gtmScript.onload = () => console.log('[GTM] Google Tag Manager script loaded')
  gtmScript.onerror = (error) => console.error('[GTM] Error while loading Google Tag Manager script ', error)
  document.head.appendChild(gtmScript)
}

const getPlatform = (isWebView: boolean) => {
  const platformMap = {
    default: 'ntv',
    mew: 'ntv-mew',
    androidphone: 'ntv-androidphone',
    androidtab: 'ntv-androidtab',
    iosphone: 'ntv-iphone',
    iostablet: 'ntv-ipad',
  }

  return isWebView && platformMap[getAppPlatform() as keyof typeof platformMap]
    ? platformMap[getAppPlatform() as keyof typeof platformMap]
    : getBreakpoint() === 'xs'
      ? platformMap['mew']
      : platformMap['default']
}

const createPageviewData = (params: GtmParams) => {
  const formatDate = function (d: string) {
    const current = new Date(d)
    const currentDate = current.toLocaleString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
    const currentTime = current.toLocaleString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    })

    return currentDate + ' ' + currentTime
  }

  const { article, gtmKeywords, sectionName } = params
  let { trackingPath } = params
  if (trackingPath === 'index/sport/ergebnisse') {
    trackingPath += '/' + location.pathname.split('/').slice(3).join('/')
  }

  const pathArr = trackingPath?.split('/')

  return {
    page_ivw_level_1: getPlatform(params.isWebView),
    page_ivw_level_2: pathArr[0] || '',
    page_ivw_level_3: pathArr[1] || '',
    page_ivw_level_4: pathArr[2] || '',
    page_ivw_level_5: pathArr[3] || '',
    article_date_published: article?.publishedAt ? formatDate(article.publishedAt) : '',
    article_headline: article ? article?.kicker + ' - ' + article?.headline : '',
    article_master_id: article?.id || '',
    article_homesection: article ? sectionName : '',
    article_master_id_new: article?.idNew || article?.id || '',
    article_has_voice: article?.hasVoice ? 'true' : 'false',
    ...(article?.type === 'gallery' ? { slide_number: 1 } : {}),
    themen_tags: gtmKeywords ? gtmKeywords.join(', ') : '',
    layout: getBreakpoint(),
  }
}

export const ga4Event = (params: {
  eventcategory?: string
  eventaction?: string | number
  eventlabel?: string
  eventvalue?: string | number
  [key: string]: string | number | undefined
}) => {
  if (window.dataLayer)
    window.dataLayer.push({
      event: 'ga4_event',
      ...params,
    })
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ga4PushMetaData = (params: GtmParams) => {
  if (window.dataLayer) window.dataLayer.push(createPageviewData(params))
}
